import React from 'react';
import { Phone, Mail, MapPin, Clock, ExternalLink } from 'lucide-react';

const Footer = () => (
  <footer data-testid="footer" className="bg-[#1A1A1A] text-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Main footer content */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 py-16 border-b border-white/8">
        {/* Brand */}
        <div className="lg:col-span-1">
          <div className="mb-5">
            <span className="font-cormorant text-2xl font-semibold text-white">
              Schulz & Gojowy
            </span>
            <span className="block font-outfit text-[10px] tracking-[0.2em] uppercase text-[#C4944A] mt-1">
              Gardinen-Atelier Berlin
            </span>
          </div>
          <p className="font-outfit text-xs text-white/50 leading-relaxed mb-5">
            Meisterbetrieb und Ausbildungsbetrieb der Raumausstatter-Innung Berlin. Seit 1988 Ihr Partner für Gardinen, Polsterei und Raumausstattung in Berlin-Heiligensee.
          </p>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#C4944A]" />
            <span className="font-outfit text-[10px] tracking-[0.15em] uppercase text-white/40">
              Meisterbetrieb · HWK Berlin Nr. 100969
            </span>
          </div>
        </div>

        {/* Leistungen */}
        <div>
          <h4 className="font-outfit text-[10px] tracking-[0.2em] uppercase text-[#C4944A] mb-5">
            Leistungen
          </h4>
          <ul className="space-y-2.5">
            {['Gardinen & Vorhänge', 'Sonnenschutz', 'Insektenschutz', 'Polsterei', 'Teppichböden', 'Gardinenwaschservice', 'Änderungsschneiderei'].map((item) => (
              <li key={item}>
                <button
                  onClick={() => document.querySelector('#leistungen')?.scrollIntoView({ behavior: 'smooth' })}
                  className="font-outfit text-xs text-white/50 hover:text-white/80 transition-colors"
                >
                  {item}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Kontakt */}
        <div>
          <h4 className="font-outfit text-[10px] tracking-[0.2em] uppercase text-[#C4944A] mb-5">
            Kontakt
          </h4>
          <ul className="space-y-3">
            <li>
              <a href="tel:+493043115080" className="flex items-start gap-2.5 group" data-testid="footer-phone">
                <Phone size={12} className="text-[#C4944A] mt-0.5 flex-shrink-0" />
                <span className="font-outfit text-xs text-white/50 group-hover:text-white/80 transition-colors">
                  030 / 4 31 15 80
                </span>
              </a>
            </li>
            <li>
              <a href="mailto:gardinenatelier@ausheiligensee.de" className="flex items-start gap-2.5 group" data-testid="footer-email">
                <Mail size={12} className="text-[#C4944A] mt-0.5 flex-shrink-0" />
                <span className="font-outfit text-xs text-white/50 group-hover:text-white/80 transition-colors break-all">
                  gardinenatelier@ausheiligensee.de
                </span>
              </a>
            </li>
            <li className="flex items-start gap-2.5" data-testid="footer-address">
              <MapPin size={12} className="text-[#C4944A] mt-0.5 flex-shrink-0" />
              <span className="font-outfit text-xs text-white/50">
                An der Schneise 57<br />
                13503 Berlin-Heiligensee
              </span>
            </li>
          </ul>
        </div>

        {/* Öffnungszeiten */}
        <div>
          <h4 className="font-outfit text-[10px] tracking-[0.2em] uppercase text-[#C4944A] mb-5">
            Öffnungszeiten
          </h4>
          <div className="space-y-2" data-testid="footer-hours">
            <div className="flex items-start gap-2.5">
              <Clock size={12} className="text-[#C4944A] mt-0.5 flex-shrink-0" />
              <div className="font-outfit text-xs text-white/50">
                <p>Montag – Freitag</p>
                <p className="text-white/40">08:30 – 13:00 Uhr</p>
                <p className="text-white/40">14:00 – 17:00 Uhr</p>
              </div>
            </div>
            <p className="font-outfit text-xs text-white/35 ml-5 mt-3 leading-relaxed">
              Beratungstermine bitte telefonisch vereinbaren.
            </p>
          </div>

          <div className="mt-6 pt-5 border-t border-white/8">
            <p className="font-outfit text-[10px] text-white/30 leading-relaxed">
              USt-IdNr.: DE135900603<br />
              Raumausstatter-Innung Berlin
            </p>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="py-6 flex flex-col sm:flex-row justify-between items-center gap-4">
        <p className="font-outfit text-[11px] text-white/30">
          © {new Date().getFullYear()} Schulz & Gojowy GbR · Alle Rechte vorbehalten
        </p>
        <div className="flex items-center gap-6">
          <a
            href="/impressum"
            data-testid="footer-impressum-link"
            className="font-outfit text-[11px] text-white/40 hover:text-white/70 transition-colors"
          >
            Impressum
          </a>
          <a
            href="/datenschutz"
            data-testid="footer-datenschutz-link"
            className="font-outfit text-[11px] text-white/40 hover:text-white/70 transition-colors"
          >
            Datenschutzerklärung
          </a>
          <a
            href="https://www.gardinenatelier-berlin.de"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-website-link"
            className="font-outfit text-[11px] text-white/40 hover:text-white/70 transition-colors flex items-center gap-1"
          >
            Website
            <ExternalLink size={10} />
          </a>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
